import { useRender } from "@base-ui-components/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[13px] font-medium transition-all active:scale-[0.97] disabled:pointer-events-none disabled:opacity-45",
  {
    variants: {
      // Every variant speaks the obsidian/shadcn token language. There is no
      // gold treatment — the legacy `brass` alias was removed in Wave 2b once
      // all call sites migrated to `default` (the primary violet look).
      variant: {
        outline:
          "border border-border bg-transparent text-foreground hover:bg-accent hover:text-foreground",
        ghost: "text-muted-foreground hover:bg-accent hover:text-foreground",
        subtle: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        danger: "bg-destructive/15 text-destructive border border-destructive/30 hover:bg-destructive/25",
        // ── Borderless obsidian primary (shadcn tokens).
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-sm",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-accent border-0",
      },
      size: {
        sm: "h-7 px-2.5 text-xs",
        md: "h-[34px] px-3.5",
        lg: "h-10 px-4 text-[13px]",
        icon: "h-7 w-7 p-0",
        iconLg: "h-9 w-9 p-0",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Merge this button's props/classes onto its single child element instead
   * of rendering a `<button>` — Base UI's `render` composition (`useRender`)
   * standing in for Radix's `asChild`/`Slot`. The public `asChild` API is
   * unchanged; only the mechanism underneath is swapped.
   */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    // `render` always takes a ReactElement here (never `undefined`), so the
    // merge always goes through Base UI's clone-and-merge path rather than
    // its "render a bare tag" path — the latter defaults `type="button"`,
    // which would break the handful of forms that rely on the *absent*
    // `type` attribute submitting on click (native browser default).
    //
    // `className` is threaded through the nested `props` bag rather than
    // `useRender`'s top-level `className` param (untyped in this Base UI
    // release) — Base UI's own merge still special-cases it against the
    // child element's className when `render` is a ReactElement, so the
    // result is identical either way.
    const computedClassName = cn(buttonVariants({ variant, size }), className);
    return useRender({
      render: asChild ? (children as React.ReactElement) : <button />,
      props: asChild
        ? { ...props, className: computedClassName }
        : { ...props, children, className: computedClassName },
      ref,
    });
  },
);
Button.displayName = "Button";
